import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { lexiqueData, getLexiqueByCategory, LexiqueEntry } from '@/data/mocks/lexique';
import {
  BookOpen,
  Search,
  Filter,
  Globe,
  TrendingUp,
  Settings,
  BarChart,
  Link as LinkIcon,
  Sparkles
} from 'lucide-react';

export const LexiquePage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'SEO':
        return <TrendingUp className="w-4 h-4" />;
      case 'GEO':
        return <Globe className="w-4 h-4" />;
      case 'Technique':
        return <Settings className="w-4 h-4" />;
      case 'Métrique':
        return <BarChart className="w-4 h-4" />;
      case 'Marketing':
        return <Sparkles className="w-4 h-4" />;
      default:
        return <BookOpen className="w-4 h-4" />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'SEO':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'GEO':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'Technique':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'Métrique':
        return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'Marketing':
        return 'bg-pink-100 text-pink-700 border-pink-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const filteredEntries = useMemo(() => {
    let entries = lexiqueData;

    // Filtrage par catégorie
    if (selectedCategory !== 'all') {
      entries = entries.filter(entry => entry.category === selectedCategory);
    }

    // Recherche
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      entries = entries.filter(entry =>
        entry.term.toLowerCase().includes(query) ||
        entry.definition.toLowerCase().includes(query)
      );
    }

    return entries;
  }, [searchQuery, selectedCategory]);

  const categories = Array.from(new Set(lexiqueData.map(e => e.category)));

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* En-tête */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col gap-4"
      >
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 flex items-center gap-3">
            <BookOpen className="w-7 h-7 sm:w-8 sm:h-8 text-stratos-gold" />
            Lexique SEO & GEO
          </h1>
          <p className="text-sm sm:text-base lg:text-lg text-gray-600 mt-2">
            Comprendre les termes techniques du référencement naturel et local
          </p>
        </div>

        {/* Statistiques */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 sm:gap-4">
          {categories.map(category => {
            const count = lexiqueData.filter(e => e.category === category).length;
            return (
              <div key={category} className="p-3 sm:p-4 bg-white rounded-lg border-2 border-gray-200 text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  {getCategoryIcon(category)}
                  <p className="text-xs sm:text-sm font-medium text-gray-700">{category}</p>
                </div>
                <p className="text-xl sm:text-2xl font-bold text-stratos-gold">{count}</p>
                <p className="text-xs text-gray-500">termes</p>
              </div>
            );
          })}
        </div>
      </motion.div>

      {/* Recherche et filtres */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card>
          <CardContent className="pt-4 sm:pt-6">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Barre de recherche */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher un terme (ex: SEO, backlinks, GMB...)"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:border-stratos-gold focus:outline-none text-sm sm:text-base"
                />
              </div>

              {/* Filtre par catégorie */}
              <div className="flex items-center gap-3">
                <Filter className="w-5 h-5 text-gray-400" />
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-stratos-gold focus:outline-none text-sm sm:text-base bg-white"
                >
                  <option value="all">Toutes catégories ({lexiqueData.length})</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>
                      {cat} ({lexiqueData.filter(e => e.category === cat).length})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Résultats */}
            <div className="mt-4 text-sm text-gray-600">
              {filteredEntries.length} terme{filteredEntries.length > 1 ? 's' : ''} affiché{filteredEntries.length > 1 ? 's' : ''}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Liste des termes */}
      <div className="grid grid-cols-1 gap-4">
        {filteredEntries.map((entry, index) => (
          <motion.div
            key={entry.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 + index * 0.02 }}
          >
            <Card className="hover:shadow-lg transition-shadow border-l-4 border-l-stratos-gold">
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="flex-1">
                    <CardTitle className="text-lg sm:text-xl flex items-center gap-3">
                      {entry.term}
                    </CardTitle>
                  </div>
                  <Badge 
                    variant="outline" 
                    className={`${getCategoryColor(entry.category)} w-fit`}
                  >
                    {getCategoryIcon(entry.category)}
                    <span className="ml-1.5">{entry.category}</span>
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Définition */}
                <div>
                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                    {entry.definition}
                  </p>
                </div>

                {/* Exemple */}
                {entry.example && (
                  <div className="bg-stratos-gold/10 p-3 sm:p-4 rounded-lg border border-stratos-gold/30">
                    <p className="text-xs font-semibold text-stratos-gold-dark mb-1">💡 Exemple concret :</p>
                    <p className="text-xs sm:text-sm text-gray-700 italic">
                      {entry.example}
                    </p>
                  </div>
                )}

                {/* Termes liés */}
                {entry.relatedTerms && entry.relatedTerms.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    <LinkIcon className="w-4 h-4 text-gray-400" />
                    <span className="text-xs text-gray-500">Voir aussi :</span>
                    {entry.relatedTerms.map((related, i) => (
                      <Badge 
                        key={i} 
                        variant="outline"
                        className="text-xs cursor-pointer hover:bg-stratos-gold/10 hover:border-stratos-gold"
                        onClick={() => setSearchQuery(related)}
                      >
                        {related}
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Message si aucun résultat */}
      {filteredEntries.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">
              Aucun terme trouvé pour "{searchQuery}"
            </p>
            <Button
              variant="outline"
              onClick={() => setSearchQuery('')}
              className="mt-4"
            >
              Réinitialiser la recherche
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};











